import React, { useCallback, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Settings as SettingsIcon,
  DeleteOutline as RemoveIcon,
} from '@mui/icons-material';
import type { DashboardWidgetInstance, DashboardTheme, WidgetGroupDirection } from './types';
import { cardStyleFromWidget, contentStyleFromWidget } from './dashboardStyleUtils';

export interface WidgetGroupContainerProps {
  childIds: string[];
  widgets: Record<string, DashboardWidgetInstance>;
  direction: WidgetGroupDirection;
  /** Flex ratios per child. Length must match childIds (or omit for equal). */
  splitRatios?: number[];
  editMode: boolean;
  theme?: DashboardTheme;
  onOpenChildSettings: (childId: string) => void;
  onRemoveChild: (childId: string) => void;
  onSplitChange?: (ratios: number[]) => void;
  renderWidgetBody: (instance: DashboardWidgetInstance, layoutItemId: string) => React.ReactNode;
}

/** Draggable divider between two children inside a widget group. */
function GroupSplitter({
  direction,
  index,
  ratios,
  onSplitChange,
  containerRef,
}: {
  direction: WidgetGroupDirection;
  index: number;
  ratios: number[];
  onSplitChange: (ratios: number[]) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const dragging = useRef(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragging.current = true;
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);

      const container = containerRef.current;
      if (!container) return;

      const handlePointerMove = (ev: PointerEvent) => {
        if (!dragging.current || !container) return;
        const rect = container.getBoundingClientRect();
        const totalSize = direction === 'row' ? rect.width : rect.height;
        const pos = direction === 'row' ? ev.clientX - rect.left : ev.clientY - rect.top;

        const totalRatio = ratios.reduce((a, b) => a + b, 0);
        const cumBefore = ratios.slice(0, index).reduce((a, b) => a + b, 0);
        const pairSum = ratios[index] + ratios[index + 1];

        const rawFrac = pos / totalSize;
        const newLeft = (rawFrac * totalRatio) - cumBefore;
        const MIN_RATIO = pairSum * 0.1;
        const clampedLeft = Math.max(MIN_RATIO, Math.min(pairSum - MIN_RATIO, newLeft));

        const next = [...ratios];
        next[index] = clampedLeft;
        next[index + 1] = pairSum - clampedLeft;
        onSplitChange(next);
      };

      const handlePointerUp = () => {
        dragging.current = false;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [direction, index, ratios, onSplitChange, containerRef],
  );

  return (
    <Box
      onPointerDown={handlePointerDown}
      sx={{
        flexShrink: 0,
        [direction === 'row' ? 'width' : 'height']: 2,
        cursor: direction === 'row' ? 'col-resize' : 'row-resize',
        bgcolor: 'divider',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'primary.main' },
        zIndex: 5,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          [direction === 'row' ? 'top' : 'left']: 0,
          [direction === 'row' ? 'bottom' : 'right']: 0,
          [direction === 'row' ? 'left' : 'top']: -6,
          [direction === 'row' ? 'right' : 'bottom']: -6,
        },
      }}
    />
  );
}

const WidgetGroupContainer: React.FC<WidgetGroupContainerProps> = ({
  childIds,
  widgets,
  direction,
  splitRatios,
  editMode,
  theme,
  onOpenChildSettings,
  onRemoveChild,
  onSplitChange,
  renderWidgetBody,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ratios = splitRatios && splitRatios.length === childIds.length
    ? splitRatios
    : childIds.map(() => 1);

  if (childIds.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, color: 'text.secondary', fontSize: 14 }}>
        Empty group — drag a widget onto this tile to add members.
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: direction,
        height: '100%',
        width: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {childIds.map((cid, idx) => {
        const child = widgets[cid];
        if (!child) return null;
        return (
          <React.Fragment key={cid}>
            {idx > 0 && onSplitChange && (
              <GroupSplitter
                direction={direction}
                index={idx - 1}
                ratios={ratios}
                onSplitChange={onSplitChange}
                containerRef={containerRef}
              />
            )}
            {idx > 0 && !onSplitChange && (
              <Box
                sx={{
                  flexShrink: 0,
                  [direction === 'row' ? 'width' : 'height']: 1,
                  bgcolor: 'divider',
                }}
              />
            )}
            <Box
              sx={{
                flex: ratios[idx],
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                ...cardStyleFromWidget(child.config?.style, theme),
                ...contentStyleFromWidget(child.config?.style, theme),
              }}
            >
              {editMode && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    zIndex: 10,
                    display: 'flex',
                    gap: 0.25,
                    bgcolor: 'rgba(0,0,0,0.45)',
                    borderRadius: 1,
                    px: 0.25,
                  }}
                >
                  <Tooltip title="Child settings">
                    <IconButton
                      size="small"
                      onClick={() => onOpenChildSettings(cid)}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{ color: '#fff' }}
                    >
                      <SettingsIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from group">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveChild(cid)}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{ color: '#fff' }}
                    >
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {renderWidgetBody(child, cid)}
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default WidgetGroupContainer;
